import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip data URI prefix
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64.replace(/\s/g, ""));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const useUpdateGym = () => {
  const queryClient = useQueryClient();

  const updateGym = async (gymId: string, name: string, address: string, logoFile: File | null = null) => {
    try {
      // Verificar que el usuario tiene permisos antes de intentar actualizar
      const { data: gymCheck, error: checkError } = await supabase
        .from("gyms")
        .select("id")
        .eq("id", gymId)
        .single();

      if (checkError || !gymCheck) {
        console.error("Permission check failed:", checkError);
        throw new Error("No tienes permisos para editar este gimnasio");
      }

      let logoUrl: string | undefined = undefined;

      // Si hay un nuevo archivo de logo, usar la edge function (bypassa RLS de storage)
      if (logoFile) {
        const fileExt = logoFile.name.split(".").pop() || "png";
        const fileName = `gym-logos/${gymId}-${Date.now()}.${fileExt}`;
        const base64 = await fileToBase64(logoFile);

        const { data: uploadData, error: uploadError } = await supabase.functions.invoke(
          "upload-gym-logo",
          {
            body: {
              imageBase64: base64,
              fileName,
              gymId,
              contentType: logoFile.type || "image/png",
            },
          }
        );

        if (uploadError) {
          console.error("Edge function upload error:", uploadError);
          throw new Error(uploadError.message || "No se pudo subir el logo");
        }

        if (!uploadData?.publicUrl) {
          throw new Error("La función no devolvió una URL pública");
        }

        logoUrl = uploadData.publicUrl;
      }

      // Actualizar nombre y dirección (la edge function ya actualizó logo_url si correspondía)
      const updateData: Record<string, unknown> = {
        name,
        address,
        updated_at: new Date().toISOString(),
      };

      if (logoUrl) {
        updateData.logo_url = logoUrl;
      }

      const { error } = await supabase
        .from("gyms")
        .update(updateData)
        .eq("id", gymId);

      if (error) {
        console.error("Error updating gym - Details:", {
          error,
          gymId,
          updateData,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
          errorHint: error.hint,
        });
        throw error;
      }

      await queryClient.invalidateQueries({ queryKey: ["managerGyms"] });
      toast({
        title: "Gimnasio actualizado",
        description: "Los cambios han sido guardados correctamente.",
      });
      return true;
    } catch (error: any) {
      console.error("Error updating gym:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "No se pudo actualizar el gimnasio.",
      });
      return false;
    }
  };

  return { updateGym };
};
